/* utils.js */

"use strict";

const _ = require("lodash");
const papaparse = require("papaparse");

const templateFileName = "device-template.csv";
const csvDefaultOptions = {
    header: true,
    delimiter: ",",
    skipEmptyLines: true,
};

/**
 * @typedef {Object} fieldDefinition
 * @property {string} title - field column title
 * @property {string} path - field path in device
 * @property {string} type - field dataType
 * @property {boolean} [required] - Indicates if field is required
 * @property {object} [typeInfo] - custom field type info
 * @property {boolean} [isCustomField] - indicates if field is custom field
 */

/**
 * @type {fieldDefinition[]}
 */
const baseFieldsData = [
    {
        title: "Name",
        path: "name",
        type: "string",
        required: true,
    },
    {
        title: "Description",
        path: "description",
        type: "string",
        required: false,
    },
    {
        title: "Management State",
        required: false,
        path: "managementState",
        type: "string",
    },
    {
        title: "Environment",
        required: false,
        path: "environment",
        type: "string",
    },
    {
        title: "IoT Device Type",
        path: "deviceType",
        type: "string",
    },
    {
        title: "Platform Name",
        path: "platformName",
        type: "string",
    },
    {
        title: "Manufacturer",
        path: "manufacturer",
        type: "string",
    },
    {
        title: "Model",
        path: "model",
        type: "string",
    },
    {
        title: "OS Name",
        path: "osName",
        type: "string",
    },
    {
        title: "OS Family",
        path: "osFamily",
        type: "string",
    },
    {
        title: "OS Release Name",
        path: "osReleaseName",
        type: "string",
    },
    {
        title: "OS Version",
        path: "osVersion",
        type: "string",
    },
    {
        title: "Machine Architecture",
        path: "machineArchitecture",
        type: "string",
    },
    {
        title: "OS Language",
        path: "language",
        type: "string",
    },
    {
        title: "Locale",
        path: "locale",
        type: "string",
    },
    {
        title: "Serial",
        path: "serialNumber",
        type: "string",
    },
    {
        title: "MAC Addresses",
        path: "macAddresses",
        type: "array",
    },
    {
        title: "Discovered Status",
        path: "offlineDiscoveryStatus",
        type: "string",
    },
    {
        title: "Discovered On",
        path: "offlineDiscoveredOn",
        type: "string",
    },
    {
        title: "Add Tags",
        path: "addTagIds",
        type: "tags",
    },
    {
        title: "Remove Tags",
        path: "removeTagIds",
        type: "tags",
    },
    {
        title: "Replace Tags",
        path: "tagIds",
        type: "tags",
    },
];

/**
 * format custom fields data
 * @param {Array<{label:string, value:string, [systemOwned]:boolean, [enabled]:boolean, [typeInfo]:Object}>} fields
 * @return {fieldDefinition[]}
 */
const formatCustomFields = (fields) =>
    fields.map((field) => ({
        title: field.label,
        path: `customFieldValues.${field.value}`,
        type: field.type,
        typeInfo: field.typeInfo,
        isCustomField: true,
    }));

const requiredFieldName = (title) => `** ${title} **`;

const getFieldsLabels = (fieldsData) =>
    fieldsData
        .filter((col) => {
            return !col.excludeFromTemplate;
        })
        .map(({title, required}) => (required ? requiredFieldName(title) : title));

const generateCSV = (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const fields = _.get(csvOptions, "columns", []);

    return papaparse.unparse({fields, data});
};

const parseCSV = async (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const resp = await papaparse.parse(data, csvOptions, (err, data) =>
        err ? Promise.reject(err) : Promise.resolve(data)
    );
    if (resp.data && resp.data[0]) {
        let respArray = [Object.keys(resp.data[0])];
        resp.data.forEach(function (item) {
            respArray.push(Object.values(item));
        });
        return Promise.resolve(respArray);
    }
    return Promise.reject(resp.errors[0] || "unknown");
};

/**
 * match headers with fields definitions
 * @param {string[]} headers
 * @param {fieldDefinition[]} fieldsDefinitions
 * @return {fieldDefinition[]}
 */
const getListOfHeadersDefinitions = (fieldsDefinitions, headers) =>
    headers.map((headerName) => {
        headerName = headerName.replace(/^\*+/, "").replace(/\*+$/, "").trim();
        return fieldsDefinitions.find(({title}) => _.lowerCase(title) === _.lowerCase(headerName));
    });

module.exports = {
    templateFileName,
    baseFieldsData,
    getFieldsLabels,
    formatCustomFields,
    generateCSV,
    parseCSV,
    getListOfHeadersDefinitions,
};
